import { PUBLIC_JOBS_HOME_URL } from '@/constants/jobsSite';
import type {
  ModulePageDefinition,
  ModulePageGroup,
  ModulePageIcon,
  ModulePageImage,
  ModulePageSlug,
} from '@/types/modulePages';

// Herkese açık modül tanıtım sayfaları: /platform/<slug>. Anasayfa ve modül sayfalarının "Platform" mega menüsü
// ile mobil/tablet hamburger menüsü bu listeden üretilir (sıra = menü sırası).
export const MODULE_PAGE_BASE_PATH = '/platform';

export const MODULE_PAGES: ModulePageDefinition[] = [
  {
    slug: 'job-management',
    group: 'recruiting',
    icon: 'arrowUpRight',
    labelKey: 'landingPage.roadmap.menuJobManagement',
    descriptionKey: 'landingPage.roadmap.menuJobManagementDesc',
    contentKey: 'jobManagement',
  },
  {
    slug: 'candidate-management',
    group: 'recruiting',
    icon: 'target',
    labelKey: 'landingPage.roadmap.menuCandidateManagement',
    descriptionKey: 'landingPage.roadmap.menuCandidateManagementDesc',
    contentKey: 'candidateManagement',
  },
  {
    slug: 'candidate-evaluation',
    group: 'recruiting',
    icon: 'dashedCircle',
    labelKey: 'landingPage.roadmap.menuCandidateEvaluation',
    descriptionKey: 'landingPage.roadmap.menuCandidateEvaluationDesc',
    contentKey: 'candidateEvaluation',
  },
  {
    slug: 'hiring-process',
    group: 'recruiting',
    icon: 'flow',
    labelKey: 'landingPage.roadmap.menuHiringProcess',
    descriptionKey: 'landingPage.roadmap.menuHiringProcessDesc',
    contentKey: 'hiringProcess',
  },
  {
    slug: 'employee-management',
    group: 'hr',
    icon: 'panel',
    labelKey: 'landingPage.roadmap.menuEmployeeManagement',
    descriptionKey: 'landingPage.roadmap.menuEmployeeManagementDesc',
    contentKey: 'employeeManagement',
  },
  {
    slug: 'performance',
    group: 'hr',
    icon: 'arrowUpRight',
    labelKey: 'landingPage.roadmap.menuPerformance',
    descriptionKey: 'landingPage.roadmap.menuPerformanceDesc',
    contentKey: 'performance',
  },
  {
    slug: 'leave-management',
    group: 'hr',
    icon: 'clock',
    labelKey: 'landingPage.roadmap.menuLeaveManagement',
    descriptionKey: 'landingPage.roadmap.menuLeaveManagementDesc',
    contentKey: 'leaveManagement',
  },
  {
    slug: 'reporting-analytics',
    group: 'hr',
    icon: 'bars',
    labelKey: 'landingPage.roadmap.menuReporting',
    descriptionKey: 'landingPage.roadmap.menuReportingDesc',
    contentKey: 'reporting',
  },
];

// Mega menü sütunları: başlık (landingPage.roadmap.<anahtar>), kart tonu ve ikon rengi.
export const MODULE_PAGE_GROUPS: {
  key: ModulePageGroup;
  titleKey: string;
  // modulePages.common.groups.<anahtar> — "İlgili modüller" kartlarındaki küçük etiket
  shortLabelKey: string;
}[] = [
  {
    key: 'recruiting',
    titleKey: 'landingPage.roadmap.menuRecruitingTitle',
    shortLabelKey: 'modulePages.common.groups.recruiting',
  },
  { key: 'hr', titleKey: 'landingPage.roadmap.menuHrTitle', shortLabelKey: 'modulePages.common.groups.hr' },
];

// Görseller anasayfadakilerle aynı (/landing/roadmap-v9).
// width/height görselin gerçek piksel boyutu; yüklenmeden önce yer ayrılsın (layout kayması olmasın) diye.
// Raporlama & Analitik'in açılışında görsel yerine kodla çizilmiş pano var.
export const MODULE_PAGE_HERO_IMAGES: Record<Exclude<ModulePageSlug, 'reporting-analytics'>, ModulePageImage> = {
  'job-management': { src: '/landing/roadmap-v9/firsatlar.webp', width: 1100, height: 807, frameHeight: 392 },
  'candidate-management': { src: '/landing/roadmap-v9/firmalar-v2.webp', width: 1100, height: 717, frameHeight: 350 },
  'candidate-evaluation': { src: '/landing/roadmap-v9/eslesme.webp', width: 1100, height: 372, frameHeight: 177 },
  'hiring-process': { src: '/landing/roadmap-v9/aday-pozisyon.webp', width: 1100, height: 361, frameHeight: 165 },
  'employee-management': {
    src: '/landing/roadmap-v9/calisan-yonetimi.webp',
    width: 1100,
    height: 821,
    frameHeight: 400,
  },
  performance: { src: '/landing/roadmap-v9/hedef-performans.webp', width: 1100, height: 528, frameHeight: 256 },
  'leave-management': { src: '/landing/roadmap-v9/izin-mesai-masraf.webp', width: 1100, height: 601, frameHeight: 291 },
};

// Tasarımların çizildiği artboard genişlikleri ve medya sorguları (anasayfanın Mobil V9 / Tablet V9 / Roadmap v9
// kırılımlarıyla aynı). Artboard ekran genişliğine ölçeklenir: mobil/tablet üst sınırlı büyür, masaüstü yalnızca küçülür.
export const PUBLIC_ARTBOARDS = {
  mobile: { mediaQuery: '(max-width: 639px)', width: 390, maxZoom: 1.25 },
  tablet: { mediaQuery: '(min-width: 640px) and (max-width: 1023px)', width: 834, maxZoom: 1.2 },
  desktop: { mediaQuery: '(min-width: 1024px)', width: 1280, maxZoom: 1 },
} as const;

export const MODULE_PAGE_LINKS = {
  login: '/auth/login',
  pricing: '/pricing',
  jobs: PUBLIC_JOBS_HOME_URL,
  home: '/',
} as const;

// Durum rozeti tonları (Badge variant adı)
export const MODULE_BADGE_TONES = ['mktBlue', 'mktAmber', 'mktGreen'] as const;

// Aday Yönetimi: kanban sütun sayaç rozetleri ve yol haritası adımlarının rozet tonları / ikonları (sıra = içerik sırası)
export const CANDIDATE_KANBAN_COLUMN_TONES = ['mktBlue', 'mktBlue', 'mktAmber', 'mktGreen'] as const;
export const CANDIDATE_TIMELINE_TONES = ['mktBlue', 'mktGreen', 'mktBlue', 'mktAmber'] as const;
export const CANDIDATE_TIMELINE_ICONS: ModulePageIcon[] = ['users', 'bullseye', 'calendar', 'message'];
export const CANDIDATE_RELATED_MODULES: ModulePageSlug[] = ['job-management', 'candidate-evaluation', 'hiring-process'];

// İlan Yönetimi: önizlemedeki ilanın örnek sayıları ve bağlantılı modüller
export const JOB_PREVIEW_STATS = { applications: 12, views: 340 } as const;
export const JOB_RELATED_MODULES: ModulePageSlug[] = ['candidate-management', 'candidate-evaluation', 'hiring-process'];

// Aday Değerlendirme: karşılaştırma tablosundaki adayların sayısal verileri (sıra = içerikteki aday sırası);
// highlighted aday tabloda beyaz satır, kartlarda mavi çerçeveyle öne çıkar.
export const EVALUATION_SKILLS = ['React', 'CSS', 'TypeScript'] as const;
export const EVALUATION_CANDIDATES = [
  { initials: 'EY', score: 87, skills: [92, 84, 80], highlighted: true },
  { initials: 'MK', score: 74, skills: [78, 70, 72], highlighted: false },
  { initials: 'SA', score: 68, skills: [64, 72, 66], highlighted: false },
] as const;
export const EVALUATION_QUESTION_TIMES = ['1:42', '2:18', '0:56'] as const;
export const EVALUATION_RELATED_MODULES: ModulePageSlug[] = ['candidate-management', 'hiring-process', 'performance'];

// İşe Alım Süreci: aşama hunisi (sıra = içerikteki aşama sırası). height: masaüstü sütun yüksekliği (px),
// width: tablet/mobil yatay bar doluluğu (%); son aşama (işe alındı) koyu mavi vurgulu.
export const HIRING_FUNNEL = [
  { icon: 'users', value: 124, height: 256, width: 100 },
  { icon: 'bullseye', value: 38, height: 127, width: 31 },
  { icon: 'document', value: 16, height: 94, width: 13 },
  { icon: 'calendar', value: 8, height: 82, width: 6 },
  { icon: 'cash', value: 3, height: 74, width: 4 },
  { icon: 'checkCircle', value: 2, height: 73, width: 4 },
] as const satisfies readonly { icon: ModulePageIcon; value: number; height: number; width: number }[];
export const HIRING_TIMELINE_ICONS: ModulePageIcon[] = ['users', 'calendar', 'cash', 'checkCircle'];
export const HIRING_TIMELINE_TONES = ['mktBlue', 'mktBlue', 'mktAmber', 'mktGreen'] as const;
export const HIRING_FEATURE_ICONS: ModulePageIcon[] = ['branch', 'calendar', 'cash', 'bell', 'users', 'chart'];
export const HIRING_RELATED_MODULES: ModulePageSlug[] = [
  'candidate-management',
  'candidate-evaluation',
  'employee-management',
];

// Çalışan Yönetimi: organizasyon şemasındaki baş harfler (sıra = içerikteki departman sırası) ve belge rozet tonları
export const EMPLOYEE_ORG_CHART = {
  root: 'CK',
  departments: [
    { managerInitials: 'SA', team: ['MK', 'BK', 'DY', 'ZB'] },
    { managerInitials: 'AT', team: ['EY', 'EÖ', 'CK'] },
    { managerInitials: 'HK', team: ['NB', 'TS', 'OY', 'İA'] },
  ],
} as const;
export const EMPLOYEE_PROFILE_INITIALS = 'EY';
export const EMPLOYEE_DOCUMENT_TONES = ['mktGreen', 'mktGreen', 'mktAmber'] as const;
export const EMPLOYEE_RELATED_MODULES: ModulePageSlug[] = ['leave-management', 'performance', 'reporting-analytics'];

// Performans: hedef ağacındaki ilerleme yüzdeleri ve baş harfler (sıra = içerikteki ekip/hedef sırası)
export const PERFORMANCE_GOAL_TREE = {
  root: 72,
  teams: [
    {
      progress: 72,
      items: [
        { initials: 'EY', progress: 80 },
        { initials: 'EÖ', progress: 64 },
      ],
    },
    {
      progress: 58,
      items: [
        { initials: 'MK', progress: 70 },
        { initials: 'BK', progress: 45 },
      ],
    },
    {
      progress: 81,
      items: [
        { initials: 'DY', progress: 90 },
        { initials: 'ZB', progress: 72 },
      ],
    },
  ],
} as const;
// 360° değerlendirme: kaynak başına 5 üzerinden ortalama puan
export const PERFORMANCE_REVIEW_SCORES = [4.2, 4.4, 4.0, 4.6] as const;
export const PERFORMANCE_REVIEW_INITIALS = 'EY';
export const PERFORMANCE_RELATED_MODULES: ModulePageSlug[] = [
  'employee-management',
  'candidate-evaluation',
  'reporting-analytics',
];

// İzin Yönetimi: ekip takvimi (12–26 Ekim, iş günleri). columns: masaüstü/tablet takvimde [başlangıç, bitiş) sütunu,
// week: mobil satırdaki 5 günlük doluluk; tone: izin türünün rengi (koyu mavi yıllık, mavi mazeret, turuncu rapor/mesai).
export const LEAVE_CALENDAR_DAYS = [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26] as const;
export const LEAVE_CALENDAR = [
  { initials: 'EY', tone: 'deep', columns: [8, 11], week: [0, 1, 1, 1, 0] },
  { initials: 'MK', tone: 'mid', columns: [3, 5], week: [0, 1, 1, 0, 0] },
  { initials: 'SA', tone: 'deep', columns: [12, 16], week: [0, 0, 1, 1, 1] },
  { initials: 'AT', tone: 'amber', columns: [10, 11], week: [0, 1, 0, 0, 0] },
] as const;
// Bakiye kartları: kullanılabilir / toplam (çubuk sayısı) ve renk tonu
export const LEAVE_BALANCES = [
  { value: 11, total: 14, tone: 'deep' },
  { value: 3, total: 5, tone: 'mid' },
  { value: 6, total: 10, tone: 'amber' },
] as const;
export const LEAVE_TIMELINE_ICONS: ModulePageIcon[] = ['calendar', 'checkCircle', 'timer', 'users'];
export const LEAVE_TIMELINE_TONES = ['mktAmber', 'mktGreen', 'mktBlue', 'mktBlue'] as const;
export const LEAVE_RELATED_MODULES: ModulePageSlug[] = ['employee-management', 'performance', 'reporting-analytics'];

// Raporlama & Analitik: hero panosundaki aylık sütun yükseklikleri (px; son ay vurgulu), panodaki grafik verileri
export const REPORTING_HERO_BARS = [61, 83, 74, 102, 93, 122, 112, 141] as const;
export const REPORTING_EMPLOYEE_LINE = '30,110 88,98 146,102 204,80 262,88 320,66 378,72 436,54';
export const REPORTING_EMPLOYEE_AREA = `30,160 ${REPORTING_EMPLOYEE_LINE} 436,160`;
// Departman dağılımı dilimleri (%; sıra = içerikteki departman sırası, renkler bileşende)
export const REPORTING_DEPARTMENT_SHARES = [38, 24, 20, 18] as const;
export const REPORTING_FUNNEL = [
  { value: 124, width: 100 },
  { value: 38, width: 31 },
  { value: 8, width: 7 },
  { value: 4, width: 3 },
] as const;
// İzin yoğunluğu ısı haritası: 2 satır × 14 gün, hücre opaklığı
export const REPORTING_HEATMAP = [
  0.1, 0.2, 0.15, 0.4, 0.3, 0.1, 0.05, 0.2, 0.5, 0.7, 0.35, 0.15, 0.1, 0.25, 0.3, 0.6, 0.8, 0.45, 0.2, 0.1, 0.15, 0.2,
  0.3, 0.35, 0.25, 0.1, 0.05, 0.1,
] as const;
export const REPORTING_EXPORT_FORMATS = ['PDF', 'XLS'] as const;
export const REPORTING_RELATED_MODULES: ModulePageSlug[] = ['employee-management', 'leave-management', 'performance'];
